import React, { useState, useEffect, useRef } from 'react';
import {
   Box,
   Button,
   Dialog,
   DialogActions,
   DialogContent,
   DialogTitle,
   TextField,
   Typography,
   Grid,
   CircularProgress,
   IconButton,
   Autocomplete,
   Paper,
   Divider,
   Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
   Close as CloseIcon,
   Add as AddIcon,
   Photo as PhotoIcon,
   AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useTrip } from '../../contexts/TripContext';
import citiesData, { City, findCityByName, searchCities } from '../../data/citiesData';
import {
   PlaceResult,
   getAccommodationsByCity,
   searchAccommodations,
} from '../../data/mockAccommodations';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { enhanceTripDescription } from '../../services/tripAIService';
import AIImageGenerator from '../../components/EventForms/AIImageGenerator';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import GooglePlacesSearch from '../common/GooglePlacesSearch';
// Import action tracking utilities
import { 
   trackUserAction, 
   trackClick, 
   trackQuickError,
   ACTION_TYPES, 
   ACTION_CATEGORIES,
   createTripMetadata,
   createPerformanceMetadata,
   createPhotoMetadata
} from '../../utils/actionTracker';

// Add Quill modules configuration
const quillModules = {
   toolbar: [
      [{ header: [2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote'],
      [{ color: [] }],
      ['link'],
      ['clean'],
   ],
   clipboard: {
      matchVisual: false,
   },
   keyboard: {
      bindings: {
         tab: false,
      },
   },
};

const quillFormats = [
   'header',
   'bold',
   'italic',
   'underline',
   'list',
   'bullet',
   'blockquote',
   'color',
   'link',
];

// Add custom styles for the editor
const editorStyles = {
   editor: {
      '.ql-container': {
         borderRadius: '0 0 4px 4px',
         backgroundColor: '#ffffff',
         fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
         fontSize: '1rem',
         lineHeight: '1.6',
      },
      '.ql-toolbar': {
         borderRadius: '4px 4px 0 0',
         backgroundColor: '#f5f5f5',
         borderColor: 'rgba(0, 0, 0, 0.23)',
      },
      '.ql-editor': {
         minHeight: '200px',
         maxHeight: '400px',
         overflowY: 'auto',
         padding: '16px',
         '&:focus': {
            boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
         },
         p: {
            marginBottom: '0.8em',
         },
         h2: {
            fontSize: '1.5rem',
            marginBottom: '1rem',
            fontWeight: 600,
         },
         h3: {
            fontSize: '1.25rem',
            marginBottom: '0.8rem',
            fontWeight: 500,
         },
         'ul, ol': {
            paddingLeft: '1.5em',
            marginBottom: '1em',
         },
         li: {
            marginBottom: '0.5em',
         },
         blockquote: {
            borderLeft: '4px solid #ccc',
            paddingLeft: '16px',
            marginLeft: '0',
            marginRight: '0',
            fontStyle: 'italic',
         },
      },
      '.ql-snow.ql-toolbar button:hover, .ql-snow .ql-toolbar button:hover': {
         color: '#1976d2',
      },
      '.ql-snow.ql-toolbar button.ql-active, .ql-snow .ql-toolbar button.ql-active': {
         color: '#1976d2',
      },
   },
};

interface Accommodation {
   name: string;
   address?: string;
   checkIn: Date | null;
   checkOut: Date | null;
   placeId?: string;
   coordinates?: [number, number];
}

interface EditTripFormProps {
   open: boolean;
   onClose: () => void;
   tripId: string;
   initialData: {
      title: string;
      description?: string;
      startDate: Date;
      endDate: Date;
      primaryDestination?: {
         name: string;
         coordinates?: [number, number];
      };
      accommodations?: Accommodation[];
      coverImageUrl?: string;
      coverPhotoUrl?: string;
      hasCoverPhoto?: boolean;
   };
}

const EditTripForm: React.FC<EditTripFormProps> = ({ open, onClose, tripId, initialData }) => {
   const { updateTrip } = useTrip();
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [saveSuccess, setSaveSuccess] = useState(false);

   // Form state
   const [title, setTitle] = useState(initialData.title || '');
   const [description, setDescription] = useState(initialData.description || '');
   const [startDate, setStartDate] = useState<Date | null>(initialData.startDate || null);
   const [endDate, setEndDate] = useState<Date | null>(initialData.endDate || null);
   const [destination, setDestination] = useState(initialData.primaryDestination?.name || '');
   const [destinationCoordinates, setDestinationCoordinates] = useState<
      [number, number] | undefined
   >(initialData.primaryDestination?.coordinates);
   const [accommodations, setAccommodations] = useState<Accommodation[]>(
      initialData.accommodations || [{ name: '', address: '', checkIn: null, checkOut: null }]
   );

   // Suggestions state
   const [citySuggestions, setCitySuggestions] = useState<City[]>([]);
   const [accommodationSuggestions, setAccommodationSuggestions] = useState<
      Record<number, PlaceResult[]>
   >({});

   // AI state
   const [enhancing, setEnhancing] = useState(false);
   const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
   const [suggestedHighlights, setSuggestedHighlights] = useState<string[]>([]);
   const [suggestedActivities, setSuggestedActivities] = useState<string[]>([]);
   const [packingTips, setPackingTips] = useState<string[]>([]);

   // Cover Photo state
   const [coverPhoto, setCoverPhoto] = useState<File | null>(null);
   const [coverPhotoUrl, setCoverPhotoUrl] = useState<string | null>(
      initialData.coverImageUrl || initialData.coverPhotoUrl || null
   );
   const [showAIGenerator, setShowAIGenerator] = useState(false);
   const fileInputRef = useRef<HTMLInputElement>(null);

   // Update form when initialData changes
   useEffect(() => {
      setTitle(initialData.title || '');
      setDescription(initialData.description || '');
      setStartDate(initialData.startDate || null);
      setEndDate(initialData.endDate || null);
      setDestination(initialData.primaryDestination?.name || '');
      setDestinationCoordinates(initialData.primaryDestination?.coordinates);
      setAccommodations(
         initialData.accommodations && initialData.accommodations.length > 0
            ? initialData.accommodations
            : [{ name: '', address: '', checkIn: null, checkOut: null }]
      );
      setCoverPhotoUrl(initialData.coverImageUrl || initialData.coverPhotoUrl || null);
   }, [initialData]);

   // Handle accommodation methods
   const handleAddAccommodation = async () => {
      setAccommodations([
         ...accommodations,
         {
            name: '',
            address: '',
            checkIn: startDate,
            checkOut: endDate,
         },
      ]);

      // Track accommodation addition
      try {
         await trackUserAction({
            actionType: 'add_accommodation',
            category: ACTION_CATEGORIES.TRIP,
            description: `Added accommodation slot in trip edit: ${title}`,
            targetId: tripId,
            targetType: 'trip',
            metadata: createTripMetadata('add_accommodation_edit', tripId, title, {
               accommodationCount: accommodations.length + 1,
               action: 'add_accommodation_edit',
            }),
         });
      } catch (trackingError) {
         console.warn('Failed to track accommodation addition:', trackingError);
      }
   };

   const handleRemoveAccommodation = async (index: number) => {
      const removedAccommodation = accommodations[index];
      const newAccommodations = [...accommodations];
      newAccommodations.splice(index, 1);
      setAccommodations(newAccommodations);

      // Track accommodation removal
      try {
         await trackUserAction({
            actionType: 'remove_accommodation',
            category: ACTION_CATEGORIES.TRIP,
            description: `Removed accommodation from trip edit: ${title}`,
            targetId: tripId,
            targetType: 'trip',
            metadata: createTripMetadata('remove_accommodation_edit', tripId, title, {
               removedAccommodationName: removedAccommodation.name,
               removedAccommodationAddress: removedAccommodation.address,
               remainingAccommodationCount: newAccommodations.length,
               action: 'remove_accommodation_edit',
            }),
         });
      } catch (trackingError) {
         console.warn('Failed to track accommodation removal:', trackingError);
      }
   };

   const handleAccommodationChange = (index: number, field: keyof Accommodation, value: any) => {
      const newAccommodations = [...accommodations];
      delete newAccommodations[index].placeId;
      newAccommodations[index] = { ...newAccommodations[index], [field]: value };
      setAccommodations(newAccommodations);
   };

   const handleAccommodationSelection = (index: number, value: PlaceResult | null) => {
      if (value) {
         const newAccommodations = [...accommodations];
         delete newAccommodations[index].placeId;

         newAccommodations[index] = {
            ...newAccommodations[index],
            address: value.formatted_address,
            coordinates: [value.geometry.location.lng, value.geometry.location.lat],
         };
         setAccommodations(newAccommodations);
      }
   };

   // Form validation
   const validateForm = () => {
      if (!title.trim()) {
         setError('Please enter a trip title');
         return false;
      }
      if (!startDate) {
         setError('Please select a start date');
         return false;
      }
      if (!endDate) {
         setError('Please select an end date');
         return false;
      }
      if (startDate > endDate) {
         setError('End date cannot be before start date');
         return false;
      }
      if (!destination) {
         setError('Please select a destination for your trip');
         return false;
      }
      if (!destinationCoordinates) {
         setError('Please select a valid destination from the dropdown list to get coordinates');
         return false;
      }
      return true;
   };

   // Add a helper function to clean HTML content
   const cleanHtmlContent = (html: string): string => {
      if (!html) return '';

      // Clean the HTML using DOMPurify with allowed tags and attributes
      let cleaned = DOMPurify.sanitize(html, {
         ALLOWED_TAGS: [
            'p',
            'br',
            'strong',
            'em',
            'u',
            'h1',
            'h2',
            'h3',
            'ul',
            'ol',
            'li',
            'blockquote',
            'a',
            'span',
         ],
         ALLOWED_ATTR: ['href', 'style'],
      });

      // Remove empty paragraphs that only contain &nbsp; or whitespace
      cleaned = cleaned.replace(/<p[^>]*>(\s|&nbsp;)*<\/p>/g, '');

      // Remove excessive line breaks while preserving intentional ones
      cleaned = cleaned.replace(/(\r\n|\n|\r){3,}/g, '\n\n');

      // Remove any style attributes that might have been added by the editor
      cleaned = cleaned.replace(/\s+style="[^"]*"/g, '');

      // Fix any double spaces
      cleaned = cleaned.replace(/\s{2,}/g, ' ');

      return cleaned.trim();
   };

   // Modify handleSubmit to clean HTML content
   const handleSubmit = async () => {
      if (!validateForm()) return;

      // Track trip edit start and gather metadata about changes
      const editStartTime = Date.now();
      const originalData = initialData;

      // Detect changes
      const changes = {
         titleChanged: title !== originalData.title,
         descriptionChanged: cleanHtmlContent(description) !== (originalData.description || ''),
         startDateChanged: startDate?.getTime() !== originalData.startDate?.getTime(),
         endDateChanged: endDate?.getTime() !== originalData.endDate?.getTime(),
         destinationChanged: destination !== (originalData.primaryDestination?.name || ''),
         accommodationsChanged:
            JSON.stringify(accommodations.filter((acc) => acc.name.trim() !== '')) !==
            JSON.stringify(initialData.accommodations || []),
         coverPhotoChanged: !!coverPhoto,
      };

      const changedFields = Object.keys(changes).filter(
         (key) => changes[key as keyof typeof changes]
      );

      const editMetadata = {
         tripId,
         originalTitle: originalData.title,
         newTitle: title,
         changedFields,
         totalChanges: changedFields.length,
         hasNewCoverPhoto: !!coverPhoto,
         accommodationCount: accommodations.filter((acc) => acc.name.trim() !== '').length,
         originalAccommodationCount: (originalData.accommodations || []).length,
         descriptionLength: cleanHtmlContent(description).length,
         originalDescriptionLength: (originalData.description || '').length,
         startTime: editStartTime,
      };

      try {
         await trackUserAction({
            actionType: ACTION_TYPES.EDIT_TRIP,
            category: ACTION_CATEGORIES.TRIP,
            description: `Started updating trip: ${title}`,
            targetId: tripId,
            targetType: 'trip',
            metadata: createTripMetadata('trip_update_start', tripId, title, {
               source: 'edit_trip_form',
               context: 'trip_editing',
               changedFields,
               stepCount: changedFields.length,
               accommodationCount: accommodations.filter((acc) => acc.name.trim() !== '').length,
               contentLength: cleanHtmlContent(description).length,
               fileSize: coverPhoto?.size,
               destination,
               hasNewCoverPhoto: !!coverPhoto,
               startTime: editStartTime,
            }),
         });
      } catch (trackingError) {
         console.warn('Failed to track trip update start:', trackingError);
      }

      setLoading(true);
      setError(null);
      setSaveSuccess(false);

      try {
         // Filter out empty accommodations
         const validAccommodations = accommodations.filter((acc) => acc.name.trim() !== '');

         // Handle cover photo upload to Firebase Storage if it exists
         let coverPhotoURL = null;
         if (coverPhoto) {
            try {
               const storage = getStorage();
               const storageRef = ref(
                  storage,
                  `trips/${tripId}/covers/${Date.now()}_${coverPhoto.name}`
               );

               await uploadBytes(storageRef, coverPhoto);

               coverPhotoURL = await getDownloadURL(storageRef);
            } catch (uploadError) {}
         }

         const cleanedDescription = cleanHtmlContent(description);

         const tripData = {
            title,
            description: cleanedDescription,
            startDate,
            endDate,
            primaryDestination: destination
               ? {
                    name: destination,
                    coordinates: destinationCoordinates || [0, 0], // Provide default coordinates if none available
                 }
               : null,
            // Adding location field for compatibility with map view
            destination: destination ? destination.split(',')[0].trim() : '',
            coordinates: destinationCoordinates
               ? {
                    latitude: destinationCoordinates[0],
                    longitude: destinationCoordinates[1],
                 }
               : null,
            accommodations: validAccommodations,
            updatedAt: serverTimestamp(),
            // Include cover photo URL if uploaded, otherwise keep existing one
            ...(coverPhotoURL
               ? {
                    coverImageUrl: coverPhotoURL,
                    coverPhotoUrl: coverPhotoURL,
                 }
               : {}),
            hasCoverPhoto:
               !!coverPhotoURL || !!(initialData.coverImageUrl || initialData.coverPhotoUrl),
         };

         const tripRef = doc(db, 'trips', tripId);
         await updateDoc(tripRef, tripData);

         // Track successful trip update
         const editEndTime = Date.now();
         const editDuration = (editEndTime - editStartTime) / 1000;

         try {
            await trackUserAction({
               actionType: ACTION_TYPES.EDIT_TRIP,
               category: ACTION_CATEGORIES.TRIP,
               description: `Successfully updated trip: ${title}`,
               targetId: tripId,
               targetType: 'trip',
               value: changedFields.length, // Number of fields changed
               metadata: createPerformanceMetadata('trip_update_success', editStartTime, {
                  source: 'edit_trip_form',
                  context: 'trip_editing',
                  targetId: tripId,
                  targetTitle: title,
                  changedFields,
                  stepCount: changedFields.length,
                  accommodationCount: validAccommodations.length,
                  contentLength: cleanedDescription.length,
                  fileSize: coverPhoto?.size,
                  fileCount: coverPhotoURL ? 1 : 0,
                  destination,
                  coverPhotoUploaded: !!coverPhotoURL,
                  changes: changes,
               }),
            });
         } catch (trackingError) {
            console.warn('Failed to track trip update success:', trackingError);
         }

         setSaveSuccess(true);

         // Try to update with the TripContext method too (if available)
         try {
            if (updateTrip) {
               await updateTrip(tripId, {
                  title,
                  description: cleanedDescription,
                  startDate: startDate as Date,
                  endDate: endDate as Date,
               });
            }
         } catch (err) {}

         // Short delay to show success message
         setTimeout(() => {
            onClose();
         }, 1000);
      } catch (err) {
         console.error('Error updating trip:', err);
         setError('Failed to update trip. Please try again.');

         // Track trip update error
         try {
            await trackQuickError(
               err instanceof Error ? err : new Error('Trip update failed'),
               'trip_update',
               {
                  ...editMetadata,
                  action: 'trip_update_error',
                  editDuration: (Date.now() - editStartTime) / 1000,
               }
            );
         } catch (trackingError) {
            console.warn('Failed to track trip update error:', trackingError);
         }
      } finally {
         setLoading(false);
      }
   };

   // Add a new function to handle AI enhancement
   const handleEnhanceWithAI = async () => {
      if (!title) {
         setError('Please enter a trip title first');
         return;
      }

      if (!destination) {
         setError('Please enter a destination first');
         return;
      }

      // Track AI enhancement start
      const enhancementStartTime = Date.now();
      const aiMetadata = {
         tripId,
         tripTitle: title,
         destination,
         hasExistingDescription: !!description.trim(),
         existingDescriptionLength: description.trim().length,
         accommodationCount: accommodations.filter((acc) => acc.name.trim() !== '').length,
         hasStartDate: !!startDate,
         hasEndDate: !!endDate,
         isEditingExistingTrip: true,
         startTime: enhancementStartTime,
      };

      try {
         await trackUserAction({
            actionType: 'ai_enhance_trip',
            category: ACTION_CATEGORIES.TRIP,
            description: `Started AI enhancement for trip edit: ${title}`,
            targetId: tripId,
            targetType: 'trip',
            metadata: {
               ...aiMetadata,
               action: 'ai_enhancement_start_edit',
            },
         });
      } catch (trackingError) {
         console.warn('Failed to track AI enhancement start:', trackingError);
      }

      try {
         setEnhancing(true);
         setError(null);

         // Filter out empty accommodations
         const validAccommodations = accommodations.filter((acc) => acc.name.trim() !== '');

         const enhancement = await enhanceTripDescription({
            title,
            description: description || undefined,
            startDate: startDate || new Date(),
            endDate: endDate || new Date(),
            destination: {
               //  placeId: destination.replace(/[^a-zA-Z0-9]/g, '-'), // Create a simple placeId from destination name
               description: destination,
               mainText: destination.split(',')[0].trim(),
               secondaryText: destination.includes(',') ? destination.split(',')[1].trim() : '',
               coordinates: {
                  lat: destinationCoordinates ? destinationCoordinates[1] : 0,
                  lng: destinationCoordinates ? destinationCoordinates[0] : 0,
               },
            },
            accommodations: validAccommodations.map((acc) => ({
               name: acc.name,
               address: acc.address,
               checkIn: acc.checkIn,
               checkOut: acc.checkOut,
            })),
            // Optional fields that could be added to the form in the future
            tripType: 'leisure', // Could be from a dropdown: leisure, business, family, etc.
            travelers: 2, // Could be a number input
            interests: ['local culture', 'food', 'history', 'photography'], // Could be a multi-select
         });

         // Update form with enhanced content
         if (enhancement.suggestedTitle) {
            setTitle(enhancement.suggestedTitle);
         }

         // Set the enhanced description
         setDescription(enhancement.enhancedDescription);

         // Update suggested tags and highlights
         setSuggestedTags(enhancement.suggestedTags || []);
         setSuggestedHighlights(enhancement.suggestedHighlights || []);
         setSuggestedActivities(enhancement.suggestedActivities || []);
         setPackingTips(enhancement.packingTips || []);

         // Track successful AI enhancement
         const enhancementEndTime = Date.now();
         const enhancementDuration = (enhancementEndTime - enhancementStartTime) / 1000;

         try {
            await trackUserAction({
               actionType: 'ai_enhance_trip',
               category: ACTION_CATEGORIES.TRIP,
               description: `Successfully enhanced trip edit with AI: ${title}`,
               targetId: tripId,
               targetType: 'trip',
               metadata: {
                  ...aiMetadata,
                  action: 'ai_enhancement_success_edit',
                  enhancementDuration,
                  endTime: enhancementEndTime,
                  titleChanged: enhancement.suggestedTitle !== title,
                  newDescriptionLength: enhancement.enhancedDescription?.length || 0,
                  suggestedTagsCount: enhancement.suggestedTags?.length || 0,
                  suggestedHighlightsCount: enhancement.suggestedHighlights?.length || 0,
                  suggestedActivitiesCount: enhancement.suggestedActivities?.length || 0,
                  packingTipsCount: enhancement.packingTips?.length || 0,
               },
            });
         } catch (trackingError) {
            console.warn('Failed to track AI enhancement success:', trackingError);
         }

         // Show success message
         setError(null);
      } catch (err) {
         console.error('Error enhancing trip:', err);
         setError('Failed to enhance trip details. Please try again.');

         // Track AI enhancement error
         try {
            await trackQuickError(
               err instanceof Error ? err : new Error('AI enhancement failed'),
               'ai_trip_enhancement_edit',
               {
                  ...aiMetadata,
                  action: 'ai_enhancement_error_edit',
                  enhancementDuration: (Date.now() - enhancementStartTime) / 1000,
               }
            );
         } catch (trackingError) {
            console.warn('Failed to track AI enhancement error:', trackingError);
         }
      } finally {
         setEnhancing(false);
      }
   };

   // Add these handler functions
   const handleFileSelect = () => {
      fileInputRef.current?.click();
   };

   const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
         const file = files[0];
         setCoverPhoto(file);
         const reader = new FileReader();
         reader.onloadend = () => {
            setCoverPhotoUrl(reader.result as string);
         };
         reader.readAsDataURL(file);

         // Track cover photo upload
         try {
            await trackUserAction({
               actionType: 'upload_cover_photo',
               category: ACTION_CATEGORIES.PHOTO,
               description: `Uploaded cover photo for trip edit: ${title}`,
               targetId: tripId,
               targetType: 'trip',
               metadata: createPhotoMetadata('upload_cover_photo_edit', undefined, file.name, {
                  source: 'edit_trip_form',
                  context: 'trip_editing',
                  associatedTripId: tripId,
                  fileSize: file.size,
                  fileTypes: [file.type],
                  fileCount: 1,
               }),
            });
         } catch (trackingError) {
            console.warn('Failed to track cover photo upload:', trackingError);
         }
      }
   };

   const handleAIImageGenerated = async (file: File | null, previewUrl: string | null) => {
      setCoverPhoto(file);
      setCoverPhotoUrl(previewUrl);
      setShowAIGenerator(false);

      // Track AI cover photo generation
      if (file) {
         try {
            await trackUserAction({
               actionType: 'generate_ai_cover_photo',
               category: ACTION_CATEGORIES.PHOTO,
               description: `Generated AI cover photo for trip edit: ${title}`,
               targetId: tripId,
               targetType: 'trip',
               metadata: {
                  tripId,
                  tripTitle: title,
                  fileName: file.name,
                  fileSize: file.size,
                  fileType: file.type,
                  isEditingTrip: true,
                  action: 'generate_ai_cover_photo_edit',
               },
            });
         } catch (trackingError) {
            console.warn('Failed to track AI cover photo generation:', trackingError);
         }
      }
   };

   return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
         <DialogTitle>
            <Box display='flex' justifyContent='space-between' alignItems='center'>
               <Typography variant='h6'>Edit Trip</Typography>
               <IconButton onClick={onClose} size='small'>
                  <CloseIcon />
               </IconButton>
            </Box>
         </DialogTitle>
         <DialogContent>
            {/* Add AI Enhancement Option */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.light', color: 'white', borderRadius: 2 }}>
               <Box display='flex' justifyContent='space-between' alignItems='center'>
                  <Box>
                     <Typography variant='h6' gutterBottom>
                        Enhance This Trip with AI
                     </Typography>
                     <Typography variant='body2'>
                        Generate a rich, personalized trip description based on your destination,
                        dates, and accommodations
                     </Typography>
                  </Box>
                  <Button
                     variant='contained'
                     color='secondary'
                     onClick={handleEnhanceWithAI}
                     disabled={enhancing || !title || !destination}
                     sx={{
                        bgcolor: 'white',
                        color: 'primary.main',
                        '&:hover': {
                           bgcolor: 'white',
                           opacity: 0.9,
                        },
                     }}
                  >
                     {enhancing ? 'Enhancing...' : 'Enhance with AI'}
                  </Button>
               </Box>
            </Paper>

            <Grid container spacing={3}>
               <Grid item xs={12}>
                  <TextField
                     label='Trip Title'
                     fullWidth
                     required
                     value={title}
                     onChange={(e) => setTitle(e.target.value)}
                     margin='normal'
                  />
               </Grid>

               <Grid item xs={12} md={6}>
                  <GooglePlacesSearch
                     value={destination}
                     searchType='places'
                     label='Destination'
                     onPlaceSelect={(place_name: any, lat: any, lng: any) => {
                        setDestinationCoordinates([lng, lat]);
                        setDestination(place_name);
                     }}
                  />
                  {/*             
            <Autocomplete
              freeSolo
              options={citySuggestions.map(city => `${city.name}, ${city.country}`)}
              onInputChange={handleCityInputChange}
              onChange={handleCityChange}
              value={destination}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Destination"
                  required
                  helperText={!destination ? "Select a valid city to add accommodations" : ""}
                  margin="normal"
                />
              )}
            /> */}
                  {!destinationCoordinates && destination && (
                     <Typography color='error' variant='caption'>
                        Warning: No coordinates found for this destination. Select a city from the
                        dropdown list.
                     </Typography>
                  )}
               </Grid>

               <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                     <Box display='flex' gap={2}>
                        <DatePicker
                           label='Start Date'
                           value={startDate}
                           onChange={(date) => setStartDate(date as Date | null)}
                           renderInput={(params) => (
                              <TextField {...params} fullWidth margin='normal' required />
                           )}
                        />
                        <DatePicker
                           label='End Date'
                           value={endDate}
                           onChange={(date) => setEndDate(date as Date | null)}
                           renderInput={(params) => (
                              <TextField {...params} fullWidth margin='normal' required />
                           )}
                        />
                     </Box>
                  </LocalizationProvider>
               </Grid>

               <Grid item xs={12}>
                  <Typography variant='subtitle1' fontWeight={600} gutterBottom>
                     Trip Description
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                     Share your experiences, highlights, and memorable moments from this trip.
                  </Typography>
                  <Paper variant='outlined' sx={{ ...editorStyles.editor }}>
                     <ReactQuill
                        value={description}
                        onChange={setDescription}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder='Start writing your trip story...'
                     />
                  </Paper>
               </Grid>
            </Grid>

            {/* Display AI suggestions if available */}
            {(suggestedHighlights.length > 0 || suggestedTags.length > 0) && (
               <Paper variant='outlined' sx={{ p: 2, mt: 3, mb: 3, borderRadius: 2 }}>
                  <Typography variant='subtitle1' fontWeight={600} gutterBottom>
                     AI Suggested Content
                  </Typography>

                  {/* Suggested highlights */}
                  {suggestedHighlights.length > 0 && (
                     <Box sx={{ mb: 2 }}>
                        <Typography
                           variant='subtitle2'
                           gutterBottom
                           color='primary.main'
                           sx={{ fontWeight: 'bold' }}
                        >
                           Trip Highlights
                        </Typography>
                        <Box sx={{ bgcolor: 'success.light', borderRadius: 1, p: 2 }}>
                           <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                              {suggestedHighlights.map((highlight, index) => (
                                 <li key={index} style={{ marginBottom: '0.5rem' }}>
                                    {highlight}
                                 </li>
                              ))}
                           </ul>
                        </Box>
                     </Box>
                  )}

                  {/* Suggested activities */}
                  {suggestedActivities.length > 0 && (
                     <Box sx={{ mb: 2 }}>
                        <Typography
                           variant='subtitle2'
                           gutterBottom
                           color='primary.main'
                           sx={{ fontWeight: 'bold' }}
                        >
                           Suggested Activities
                        </Typography>
                        <Box sx={{ bgcolor: 'info.light', borderRadius: 1, p: 2 }}>
                           <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                              {suggestedActivities.map((activity, index) => (
                                 <li key={index} style={{ marginBottom: '0.5rem' }}>
                                    {activity}
                                 </li>
                              ))}
                           </ul>
                        </Box>
                     </Box>
                  )}

                  {/* Suggested tags */}
                  {suggestedTags.length > 0 && (
                     <Box>
                        <Typography
                           variant='subtitle2'
                           gutterBottom
                           color='primary.main'
                           sx={{ fontWeight: 'bold' }}
                        >
                           Suggested Tags
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                           {suggestedTags.map((tag, index) => (
                              <Chip key={index} label={tag} size='small' />
                           ))}
                        </Box>
                     </Box>
                  )}
               </Paper>
            )}

            {/* Accommodations Section */}
            <Box mt={4} mb={2}>
               <Typography variant='h6' gutterBottom>
                  Accommodations
               </Typography>
               <Typography variant='body2' color='text.secondary' gutterBottom>
                  Add where you stayed or plan to stay during this trip
               </Typography>
            </Box>

            {accommodations.map((accommodation, index) => (
               <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                     <Typography variant='subtitle1'>Accommodation {index + 1}</Typography>
                     {accommodations.length > 1 && (
                        <IconButton
                           size='small'
                           onClick={() => handleRemoveAccommodation(index)}
                           color='error'
                        >
                           <CloseIcon />
                        </IconButton>
                     )}
                  </Box>

                  <Grid container spacing={2}>
                     <Grid item xs={12} md={6}>
                        <TextField
                           label='Name'
                           fullWidth
                           margin='normal'
                           value={accommodation.name || ''}
                           onChange={(e) =>
                              handleAccommodationChange(index, 'name', e.target.value)
                           }
                        />
                        {/* <Autocomplete
                           freeSolo
                           options={accommodationSuggestions[index] || []}
                           getOptionLabel={(option) =>
                              typeof option === 'string' ? option : option.name
                           }
                           onInputChange={(event, value) => {
                              handleAccommodationInputChange(index, value);
                              handleAccommodationChange(index, 'name', value);
                           }}
                           onChange={(event, value) =>
                              handleAccommodationSelection(index, value as PlaceResult | null)
                           }
                           value={accommodation.name}
                           renderInput={(params) => (
                              <TextField
                                 {...params}
                                 label='Accommodation Name'
                                 fullWidth
                                 margin='normal'
                              />
                           )}
                        /> */}
                     </Grid>
                     <Grid item xs={12} md={6}>
                        <GooglePlacesSearch
                           value={accommodation.address || ''}
                           searchType='places'
                           label='Address'
                           onPlaceSelect={(place_name: any, lat: any, lng: any) => {
                              handleAccommodationSelection(index, {
                                 name: place_name,
                                 formatted_address: place_name,
                                 geometry: {
                                    location: {
                                       lat,
                                       lng,
                                    },
                                 },
                              } as PlaceResult | null);
                           }}
                        />
                        {/* <TextField
                           label='Address'
                           fullWidth
                           value={accommodation.address || ''}
                           onChange={(e) =>
                              handleAccommodationChange(index, 'address', e.target.value)
                           }
                           margin='normal'
                        /> */}
                     </Grid>
                  </Grid>

                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                     <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                           <DatePicker
                              label='Check-in Date'
                              value={accommodation.checkIn}
                              onChange={(date) =>
                                 handleAccommodationChange(index, 'checkIn', date as Date | null)
                              }
                              renderInput={(params) => (
                                 <TextField {...params} fullWidth margin='normal' />
                              )}
                           />
                        </Grid>
                        <Grid item xs={12} md={6}>
                           <DatePicker
                              label='Check-out Date'
                              value={accommodation.checkOut}
                              onChange={(date) =>
                                 handleAccommodationChange(index, 'checkOut', date as Date | null)
                              }
                              renderInput={(params) => (
                                 <TextField {...params} fullWidth margin='normal' />
                              )}
                           />
                        </Grid>
                     </Grid>
                  </LocalizationProvider>
               </Paper>
            ))}

            <Button
               startIcon={<AddIcon />}
               onClick={handleAddAccommodation}
               variant='outlined'
               sx={{ mt: 1, mb: 3 }}
            >
               Add Accommodation
            </Button>

            {error && (
               <Box mt={2} mb={2}>
                  <Typography color='error'>{error}</Typography>
               </Box>
            )}

            {saveSuccess && (
               <Box mt={2} mb={2}>
                  <Typography color='success.main'>Trip successfully updated!</Typography>
               </Box>
            )}

            {/* Cover Photo Display Section */}
            <Paper variant='outlined' sx={{ p: 2, mb: 3, borderRadius: 2 }}>
               <Typography variant='subtitle1' fontWeight={600} gutterBottom>
                  Cover Photo
               </Typography>
               <Box sx={{ mb: 2 }}>
                  {coverPhotoUrl ? (
                     <Box sx={{ position: 'relative' }}>
                        <img
                           src={coverPhotoUrl}
                           alt='Trip Cover'
                           style={{
                              width: '100%',
                              height: '240px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                           }}
                        />
                        <Button
                           variant='contained'
                           color='error'
                           size='small'
                           sx={{ position: 'absolute', top: 10, right: 10 }}
                           onClick={() => {
                              setCoverPhoto(null);
                              setCoverPhotoUrl(null);
                           }}
                        >
                           Remove
                        </Button>
                     </Box>
                  ) : (
                     <Box
                        sx={{
                           width: '100%',
                           height: '160px',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           backgroundColor: 'grey.100',
                           borderRadius: '8px',
                           border: '1px dashed grey.400',
                        }}
                     >
                        <Typography color='text.secondary'>No cover photo selected</Typography>
                     </Box>
                  )}
               </Box>

               <Grid container spacing={1}>
                  <Grid item xs={6}>
                     <Button
                        startIcon={<PhotoIcon />}
                        variant='outlined'
                        fullWidth
                        onClick={handleFileSelect}
                     >
                        Upload Photo
                     </Button>
                  </Grid>
                  <Grid item xs={6}>
                     <Button
                        startIcon={<AutoAwesomeIcon />}
                        variant='outlined'
                        color='secondary'
                        fullWidth
                        onClick={() => setShowAIGenerator(true)}
                     >
                        Generate with AI
                     </Button>
                  </Grid>
               </Grid>
            </Paper>
         </DialogContent>
         <DialogActions>
            <Button onClick={onClose} disabled={loading}>
               Cancel
            </Button>
            <Button onClick={handleSubmit} variant='contained' color='primary' disabled={loading}>
               {loading ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
         </DialogActions>

         {/* Hidden file input */}
         <input
            type='file'
            accept='image/*'
            hidden
            ref={fileInputRef}
            onChange={handleFileChange}
         />

         {/* AI Image Generator */}
         <AIImageGenerator
            open={showAIGenerator}
            onClose={() => setShowAIGenerator(false)}
            eventTitle={title || 'Trip'}
            eventDescription={description || ''}
            eventType={'travel'}
            onImageGenerated={handleAIImageGenerated}
         />
      </Dialog>
   );
};

export default EditTripForm;
